import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {OrgDto} from "../dto/org.dto";
import {LetterPriceDto} from "../dto/letter-price.dto";
import {UrlBuilder} from "../item/url.item";
import {WrapperDto} from "../dto/wrapper.dto";
import {map} from "rxjs/operators";
import {Observable, tap} from "rxjs";
import {VoidDto} from "../dto/void.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {SearchCorrespondenceRes} from "../dto/search/search-correspondence.res";
import {EmailSendDto} from "../dto/email-send.dto";
import {MediaFileType} from "../../libs/enum/media-file-type.enum";
import {SendLetterDto} from "../dto/send-letter.dto";
import {BounceDto} from "../dto/bounce.dto";

@Injectable({providedIn: 'root'})
export class CorrespondenceRepo {

  private readonly invoiceLetterPriceRead = '/v1/org/{orgId}/correspondence/invoice/{invoiceId}/letter/price';
  private readonly deliveryNoteLetterPriceRead = '/v1/org/{orgId}/correspondence/deliverynote/{deliveryNoteId}/letter/price';
  private readonly proformaInvoiceLetterPriceRead = '/v1/org/{orgId}/correspondence/proformainvoice/{proformaInvoiceId}/letter/price';
  private readonly letterPriceRead = '/v1/org/{orgId}/correspondence/type/{type}/contact/{contactId}/address/{addressId}/letter/price';
  private readonly invoiceLetterSendWrite = '/v1/org/{orgId}/correspondence/invoice/{invoiceId}/letter/send';
  private readonly deliveryNoteLetterSendWrite = '/v1/org/{orgId}/correspondence/deliverynote/{deliveryNoteId}/letter/send';
  private readonly proformaInvoiceLetterSendWrite = '/v1/org/{orgId}/correspondence/proformainvoice/{proformaInvoiceId}/letter/send';
  private readonly letterSendWrite = '/v1/org/{orgId}/correspondence/letter/send';
  private readonly emailSendWrite = '/v1/org/{orgId}/correspondence/email/send';
  private readonly correspondenceSearch = '/v1/org/{orgId}/correspondence/search';
  private readonly correspondenceBounceRead = '/v1/org/{orgId}/correspondence/bounce';

  private readonly http: HttpClient = inject(HttpClient);

  // ##########################################################################
  // #### Calculate Letter
  // ##########################################################################

  public getInvoicePrice(org: OrgDto, invoiceId: string, settings: LetterPriceDto): Observable<WrapperDto<LetterPriceDto>> {
    const url = UrlBuilder.builder().addPath(this.invoiceLetterPriceRead)
      .addParam('orgId', org.id).addParam('invoiceId', invoiceId)
      .addQueryParams('spectrum', settings.printSpectrum).addQueryParams('mode', settings.printMode)
      .addQueryParams('delivery', settings.deliveryProduct).build();
    return this.http.get<WrapperDto<LetterPriceDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public getDeliveryNotePrice(org: OrgDto, deliveryNoteId: string, settings: LetterPriceDto): Observable<WrapperDto<LetterPriceDto>> {
    const url = UrlBuilder.builder().addPath(this.deliveryNoteLetterPriceRead)
      .addParam('orgId', org.id).addParam('deliveryNoteId', deliveryNoteId)
      .addQueryParams('spectrum', settings.printSpectrum).addQueryParams('mode', settings.printMode)
      .addQueryParams('delivery', settings.deliveryProduct).build();
    return this.http.get<WrapperDto<LetterPriceDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public getProformaInvoicePrice(org: OrgDto, proformaInvoiceId: string, settings: LetterPriceDto): Observable<WrapperDto<LetterPriceDto>> {
    const url = UrlBuilder.builder().addPath(this.proformaInvoiceLetterPriceRead)
      .addParam('orgId', org.id).addParam('proformaInvoiceId', proformaInvoiceId)
      .addQueryParams('spectrum', settings.printSpectrum).addQueryParams('mode', settings.printMode)
      .addQueryParams('delivery', settings.deliveryProduct).build();
    return this.http.get<WrapperDto<LetterPriceDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public getLetterPrice(org: OrgDto, contactId: string, addressId: string, type: MediaFileType, settings: LetterPriceDto): Observable<WrapperDto<LetterPriceDto>> {
    const url = UrlBuilder.builder().addPath(this.letterPriceRead).addParam('orgId', org.id)
      .addParam('contactId', contactId).addParam('addressId', addressId).addParam('type', type)
      .addQueryParams('spectrum', settings.printSpectrum).addQueryParams('mode', settings.printMode)
      .addQueryParams('delivery', settings.deliveryProduct).build();
    return this.http.get<WrapperDto<LetterPriceDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Send Letter
  // ##########################################################################

  public sendInvoiceLetter(org: OrgDto, invoiceId: string, settings: LetterPriceDto): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.invoiceLetterSendWrite)
      .addParam('orgId', org.id).addParam('invoiceId', invoiceId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), settings, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public sendDeliveryNoteLetter(org: OrgDto, deliveryNoteId: string, settings: LetterPriceDto): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.deliveryNoteLetterSendWrite)
      .addParam('orgId', org.id).addParam('deliveryNoteId', deliveryNoteId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), settings, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public sendProformaInvoiceLetter(org: OrgDto, proformaInvoiceId: string, settings: LetterPriceDto): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.proformaInvoiceLetterSendWrite)
      .addParam('orgId', org.id).addParam('proformaInvoiceId', proformaInvoiceId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), settings, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public sendLetter(org: OrgDto, dto: SendLetterDto): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.letterSendWrite).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), dto, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Send Email
  // ##########################################################################

  public sendEmail(org: OrgDto, dto: EmailSendDto): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.emailSendWrite)
      .addParam('orgId', org.id).addParam('type', dto.type).addParam('contactId', dto.contactId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), dto, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Bounce
  // ##########################################################################

  public getBounces(org: OrgDto): Observable<WrapperDto<BounceDto>> {
    const url = UrlBuilder.builder().addPath(this.correspondenceBounceRead).addParam('orgId', org.id).build();
    return this.http.get<WrapperDto<BounceDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Search
  // ##########################################################################

  public search(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchCorrespondenceRes>> {
    const url = UrlBuilder.builder().addPath(this.correspondenceSearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchCorrespondenceRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchCorrespondenceRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }
}
